import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { cache } from '@common/util/cache.operator';
import { BuildProfileTab } from '@portal-core/builds/enums/build-profile-tab.enum';
import { ProcessBuildType } from '@portal-core/builds/enums/process-build-type.enum';
import { Build } from '@portal-core/builds/models/build.model';
import { BuildsApiService } from '@portal-core/builds/services/builds-api.service';
import { BuildsUIService } from '@portal-core/builds/services/builds-ui.service';
import { BuildsService } from '@portal-core/builds/services/builds.service';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ConfirmDialogComponent } from '@portal-core/general/components/confirm-dialog/confirm-dialog.component';
import { LiveLogSourceLinkResolver } from '@portal-core/live-log/components/live-log/live-log.component';
import { LogTabPanelComponent } from '@portal-core/live-log/components/log-tab-panel/log-tab-panel.component';
import { LogPrepareStatus } from '@portal-core/live-log/enums/log-prepare-status.enum';
import { LogStreamService } from '@portal-core/live-log/services/log-stream.service';
import { CentralPermissions } from '@portal-core/permissions/enums/central-permissions.enum';
import { PermissionsService } from '@portal-core/permissions/services/permissions.service';
import { ProcessAttemptStatus } from '@portal-core/processes/enums/process-attempt-status.enum';
import { ProfilesService } from '@portal-core/profiles/services/profiles.service';
import { ProjectStatus } from '@portal-core/projects/enums/project-status.enum';
import { Project } from '@portal-core/projects/models/project.model';
import { ProjectsService } from '@portal-core/projects/services/projects.service';
import { RoutingService } from '@portal-core/routing/services/routing.service';
import { TargetsService } from '@portal-core/targets/services/targets.service';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { SnackBarAutoDismissDurationMS } from '@portal-core/ui/snack-bar/constants/snack-bar-auto-dismiss-duration.constant';
import { LoadingState } from '@portal-core/util/loading-state';
import { pollUntil } from '@portal-core/util/polling';
import { BehaviorSubject, catchError, combineLatest, filter, first, map, noop, Observable, of, Subscription, switchMap } from 'rxjs';

export interface BuildProfileDialogData {
  buildId: number;
  buildProfileTab?: BuildProfileTab;
}

enum BuildProfileForm {
  Delete,
  Settings
}

@Component({
  selector: 'mc-build-profile-dialog',
  templateUrl: './build-profile-dialog.component.html',
  styleUrls: ['./build-profile-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuildProfileDialogComponent extends DialogBase implements OnDestroy, OnInit {
  /** Defines the default config for the dialog. */
  static DialogConfig: MatDialogConfig = {
    width: '120rem',
    height: '68rem',
    // Keeps focus off the floating maximize/close buttons on open (no mcDialogAutoFocus target here).
    autoFocus: 'dialog',
  };

  BuildProfileForm: typeof BuildProfileForm = BuildProfileForm;
  BuildProfileTab: typeof BuildProfileTab = BuildProfileTab;
  ProcessAttemptStatus: typeof ProcessAttemptStatus = ProcessAttemptStatus;

  build$: Observable<Build>;
  buildId: number;
  buildProfileTab: BuildProfileTab;
  // Set after the prepare endpoint reports `Ready`. Bound into `<mc-live-log [streamUrl]>`.
  // The live-log / log-stream-grid children only open EventSource once this becomes truthy
  // (their `ngOnChanges` ignores undefined). The shared LogStreamService caches the multicast
  // keyed by URL, so the anchor subscription and the children end up sharing one EventSource.
  logStreamUrl: string;
  // Tracks the prepare-endpoint polling: loading$ during the polling window, generalError$ /
  // detailedErrors$ if a request fails or polling times out. The child surfaces (live-log,
  // log-stream-grid) read these to render the loader / error panel.
  logPrepareLoadingState: LoadingState<string> = new LoadingState<string>();
  // True when the prepare endpoint reported `Unavailable` — no log artifacts on disk and Redis
  // is empty. Children render "No log is available for this build." in their empty state.
  // A BehaviorSubject (rather than a plain field) so canDownloadLog$ can react to it once the
  // prepare-endpoint poll resolves, since build.CanDownloadLog alone doesn't know about it.
  logUnavailable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  get emptyLabelOverride(): string | null {
    if (this.logUnavailable$.value) return 'No log is available for this build.';
    return null;
  }

  // Captured from the latest build$/project$ emissions so `resolveBuildSourceLink` (a stable
  // arrow function bound into `<mc-live-log [sourceLinkResolver]>`) has the context it needs
  // without re-subscribing each line.
  private currentBuild: Build | null = null;
  private currentProject: Project | null = null;

  // Build the workspace router link for a source-file annotation, deferring to the running
  // application's RoutingService so apps without a workspace file route (e.g. portal-admin)
  // can opt out by returning null. Falls back to the default workspace path shape used by
  // branch-avatar / toc-avatar / etc. when the handler returns undefined. Defined as an arrow
  // property so the input binding stays referentially stable.
  resolveBuildSourceLink: LiveLogSourceLinkResolver = (src: string) => {
    if (!src || !this.currentBuild || !this.currentProject || this.currentProject.LicenseId === undefined) {
      return null;
    }
    const path = this.routingService.getProjectFilePath(this.currentProject.LicenseId, this.currentProject.Id, this.currentBuild.BranchName, src);
    return typeof path === 'undefined'
      ? ['/', this.currentProject.LicenseId, 'projects', this.currentProject.Id, 'workspace', 'file', encodeURIComponent(this.currentBuild.BranchName), encodeURIComponent(src)]
      : path;
  };
  // The Log tab's shared UI (Download/Find/Settings buttons + Log/Grid sub-tabs) — used by the
  // maximize button handler to re-measure the live-log's virtual scroll viewport after a
  // programmatic dialog resize.
  @ViewChild(LogTabPanelComponent) logTabPanel: LogTabPanelComponent;

  licenseId$: Observable<number>;
  loadingState: LoadingState<string> = new LoadingState<string>();
  project$: Observable<Project>;
  projectIsLocked$: Observable<boolean>;
  reload$: BehaviorSubject<void> = new BehaviorSubject(undefined);
  routerEventSubscription: Subscription;
  logStreamAnchorSubscription: Subscription;
  logPrepareSubscription: Subscription;
  currentBuildSubscription: Subscription;
  currentProjectSubscription: Subscription;
  showCancelBuildTab$: Observable<boolean>;
  showDeleteTab$: Observable<boolean>;
  showDownloadBuildTab$: Observable<boolean>;
  canDownloadLog$: Observable<boolean>;
  showLogTab$: Observable<boolean>;
  showSettingsTab$: Observable<boolean>;
  showViewOutputTab$: Observable<boolean>;
  userCanDeleteBuilds$: Observable<boolean>;
  userCanDownloadBuilds$: Observable<boolean>;
  userCanManageBuilds$: Observable<boolean>;
  userCanRunScheduleBuilds$: Observable<boolean>;

  constructor(
    protected dialog: MatDialog,
    protected dialogRef: MatDialogRef<BuildProfileDialogComponent>,
    private router: Router,
    private snackBar: MatSnackBar,
    private errorService: ErrorService,
    private buildsService: BuildsService,
    private targetsService: TargetsService,
    private buildsUIService: BuildsUIService,
    private projectsService: ProjectsService,
    private permissionsService: PermissionsService,
    private profilesService: ProfilesService,
    private changeDetectorRef: ChangeDetectorRef,
    private logStreamService: LogStreamService,
    private buildsApiService: BuildsApiService,
    private routingService: RoutingService,
    @Inject(MAT_DIALOG_DATA) private data: BuildProfileDialogData
  ) {
    super(dialog, dialogRef);
  }

  ngOnInit() {
    super.ngOnInit();

    this.buildId = this.data.buildId;

    // Create an observable of the build. Start with reload$ so that the data can be reloaded as needed
    this.build$ = this.reload$.pipe(
      switchMap(() => this.profilesService.getBuildProfile$(this.buildId)),
      // Switch to the build in the data store. The build is returned in the profile data so there is no need to make a request to the API
      switchMap(build => this.buildsService.getItemById$(build.Id, { allowApiRequest: false })),
      cache()
    );

    // Create an observable for the build's project id
    const projectId$ = this.build$.pipe(
      map(build => build.ProjectId)
    );

    this.project$ = projectId$.pipe(
      // The project is returned in the profile data so there is no need to make a request to the API
      switchMap(projectId => this.projectsService.getItemById$(projectId, { allowApiRequest: false })),
      cache()
    );

    // Create an observable for the license id. The build doesn't have a license id so grab it from the project.
    this.licenseId$ = this.project$.pipe(
      map(project => project?.LicenseId)
    );

    // Create an observable for locked status of the build's project
    this.projectIsLocked$ = this.project$.pipe(
      map(project => project?.Status === ProjectStatus.Locked)
    );

    // Create an observable for the user's access to the build's project
    const userHasProjectAccess$ = this.project$.pipe(
      map(project => project?.UserHasAccess)
    );

    // Create observables for the user's permissions
    this.userCanManageBuilds$ = projectId$.pipe(
      switchMap(projectId => this.permissionsService.currentUserHasPermission$(CentralPermissions.ManageBuilds, projectId))
    );
    this.userCanDeleteBuilds$ = projectId$.pipe(
      switchMap(projectId => this.permissionsService.currentUserHasPermission$(CentralPermissions.DeleteBuilds, projectId))
    );
    this.userCanDownloadBuilds$ = projectId$.pipe(
      switchMap(projectId => this.permissionsService.currentUserHasPermission$(CentralPermissions.DownloadBuilds, projectId))
    );
    this.userCanRunScheduleBuilds$ = projectId$.pipe(
      switchMap(projectId => this.permissionsService.currentUserHasPermission$(CentralPermissions.RunOrScheduleBuilds, projectId))
    );

    // Create observables for which tabs to show in the sidebar
    this.showCancelBuildTab$ = combineLatest([this.userCanRunScheduleBuilds$, userHasProjectAccess$, this.build$, this.projectIsLocked$]).pipe(
      map(([userCanRunScheduleBuilds, userHasProjectAccess, build, projectIsLocked]) => userCanRunScheduleBuilds && userHasProjectAccess && build.CanBeCanceled && !projectIsLocked)
    );
    this.showDeleteTab$ = combineLatest([this.userCanDeleteBuilds$, userHasProjectAccess$, this.build$, this.projectIsLocked$]).pipe(
      map(([userCanDeleteBuilds, userHasProjectAccess, build, projectIsLocked]) => userCanDeleteBuilds && userHasProjectAccess && build.IsFinished && !projectIsLocked)
    );
    this.showDownloadBuildTab$ = combineLatest([this.userCanDownloadBuilds$, userHasProjectAccess$, this.build$]).pipe(
      map(([userCanDownloadBuilds, userHasProjectAccess, build]) => userCanDownloadBuilds && userHasProjectAccess && build.CanDownload)
    );

    const userCanReadLog$: Observable<boolean> = this.buildsService.getAccess$({ Resource: { Id: this.buildId }, Operations: ['LogRead'] }).pipe(
      map(access => !!access.LogRead),
      // Hide the tab rather than failing the whole dialog if the access check itself errors.
      catchError(() => of(false)),
      cache()
    );
    this.canDownloadLog$ = combineLatest([userCanReadLog$, this.build$, this.logUnavailable$]).pipe(
      map(([userCanReadLog, build, logUnavailable]) => userCanReadLog && build.CanDownloadLog && !logUnavailable)
    );
    this.showLogTab$ = userCanReadLog$;
    this.showSettingsTab$ = combineLatest([this.userCanManageBuilds$, userHasProjectAccess$, this.projectIsLocked$]).pipe(
      map(([userCanManageBuilds, userHasProjectAccess, projectIsLocked]) => userCanManageBuilds && userHasProjectAccess && !projectIsLocked)
    );
    this.showViewOutputTab$ = combineLatest([this.userCanDownloadBuilds$, userHasProjectAccess$, this.build$]).pipe(
      map(([userCanDownloadBuilds, userHasProjectAccess, build]) => userCanDownloadBuilds && userHasProjectAccess && build.CanViewOutput)
    );

    this.loadingState.update(true);

    // Start the initial load
    combineLatest([
      this.build$,
      this.projectIsLocked$,
      userHasProjectAccess$,
      this.userCanManageBuilds$,
      this.userCanDeleteBuilds$,
      this.userCanDownloadBuilds$,
      this.userCanRunScheduleBuilds$,
      userCanReadLog$
    ]).pipe(
      first()
    ).subscribe(() => {
      // Set the initial tab
      if (typeof this.data.buildProfileTab === 'number') {
        this.buildProfileTab = this.data.buildProfileTab;
      } else {
        this.buildProfileTab = BuildProfileTab.Overview;
      }

      this.loadingState.update(false);
    }, error => {
      this.loadingState.update(false, 'Unable to load the build.', this.errorService.getErrorMessages(error));
    });

    // Resolve the log via the prepare endpoint before opening the SSE stream. Tier 1: the build
    // is running or just finished, Redis is hot, Ready on first call. Tier 2: Redis was evicted
    // but a `.mcjlog` snapshot exists on blob — the resolver loads it into Redis and returns
    // Ready. Tier 3: a legacy `.mclog` exists but no `.mcjlog` yet — the resolver enqueues the
    // converter and returns Loading; we keep polling until the worker writes the snapshot and
    // the next poll lands in tier 2. No log artifacts at all → Unavailable. While polling, the
    // children show "Loading historical log…" in their empty state.
    this.logPrepareLoadingState.update(true);
    this.logPrepareSubscription = pollUntil(
      () => this.buildsApiService.prepareBuildLog$(this.buildId),
      response => response.Status === LogPrepareStatus.Ready || response.Status === LogPrepareStatus.Unavailable,
      { delayMs: 2000, timeoutMs: 120_000, emitWhenDoneOnly: true }
    ).subscribe({
      next: response => {
        this.logPrepareLoadingState.update(false);
        if (response.Status === LogPrepareStatus.Ready) {
          this.logStreamUrl = this.buildsApiService.getBuildLogStreamUrl(this.buildId);
          this.logStreamAnchorSubscription = this.logStreamService.getStream$(this.logStreamUrl).subscribe();
        } else {
          this.logUnavailable$.next(true);
        }
        // OnPush parent: setting the field doesn't itself trigger CD, so the child's
        // [streamUrl] / [loading] inputs wouldn't repaint until the next external trigger
        // (e.g. tab switch). markForCheck flushes the bindings now.
        this.changeDetectorRef.markForCheck();
      },
      error: error => {
        const isTimeout = error?.name === 'TimeoutError';
        this.logPrepareLoadingState.update(
          false,
          isTimeout ? 'Log preparation is taking longer than expected. Try again shortly.' : 'Unable to load the log.',
          isTimeout ? undefined : this.errorService.getErrorMessages(error)
        );
        this.changeDetectorRef.markForCheck();
      }
    });

    // Capture the latest build / project for the source-link resolver. cache() above ensures
    // these are shareReplay'd, so subscribing here doesn't trigger duplicate API requests.
    this.currentBuildSubscription = this.build$.subscribe(build => this.currentBuild = build);
    this.currentProjectSubscription = this.project$.subscribe(project => this.currentProject = project);

    // Listen for successful navigations and close the dialog (for closing the dialog when the user clicks a link in the dialog)
    this.routerEventSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => this.dialogRef.close());
  }

  ngOnDestroy() {
    this.logStreamAnchorSubscription?.unsubscribe();
    // Completed streams are only ever released explicitly (see LogStreamService) — this dialog
    // closing is the boundary that says the shared stream is no longer needed by anyone.
    if (this.logStreamUrl) {
      this.logStreamService.release(this.logStreamUrl);
    }
    this.logPrepareSubscription?.unsubscribe();
    this.routerEventSubscription?.unsubscribe();
    this.currentBuildSubscription?.unsubscribe();
    this.currentProjectSubscription?.unsubscribe();
  }

  onCancelBuildClicked(build: Build) {
    this.dialog.open(ConfirmDialogComponent, {
      width: '36rem',
      data: {
        prompt: 'Are you sure you want to cancel this build?',
        action: 'OK'
      }
    }).afterClosed().pipe(
      filter(confirmed => confirmed),
      switchMap(() => this.buildsService.processBuild$(build.Id, ProcessBuildType.Cancel))
    ).subscribe(noop, () => {
      this.snackBar.open('Unable to cancel the build.', 'OK', { duration: SnackBarAutoDismissDurationMS });
    });
  }

  onDownloadLogClicked(build: Build) {
    const logUrl = build.PrivateOutputUrl.substring(0, build.PrivateOutputUrl.lastIndexOf('/') + 1) + this.getTargetFileName(build.TargetPath) + '.mclog';
    window.open(logUrl, '_blank');
  }

  getTargetFileName(targetPath: string): string {
    return this.targetsService.getTargetFileNameFromPath(targetPath);
  }

  onDownloadBuildClicked(build: Build) {
    this.buildsUIService.downloadBuild(build);
  }

  onViewOutputClicked(build: Build) {
    this.buildsUIService.viewOutput(build);
  }

  onCancel() {
    this.closeDialog();
  }

  onSaved(buildProfileForm: BuildProfileForm) {
    switch (buildProfileForm) {
      case BuildProfileForm.Delete:
        this.closeDialog();
        break;
      // If the settings were updated then refresh the build data to get the latest data
      case BuildProfileForm.Settings:
        this.reload();
        break;
    }
  }

  getColorForBuildState(build: Build): string {
    return this.buildsService.getColorForBuild(build, 'muted');
  }

  getTextColorClassForBuildState(build: Build): string {
    return `mc-text-${this.getColorForBuildState(build)}`;
  }

  getStatusDisplayText(build: Build): string {
    return this.buildsService.getStatusDisplayText(build);
  }

  reload() {
    this.reload$.next();
  }
}
